import { FC, useEffect, useRef } from 'react'
import { Package, Tags, TrendingUp, Barcode, Boxes, ShoppingCart } from 'lucide-react'

const features = [
  {
    icon: Package,
    color: 'from-sumoPrimary to-sumoAmber',
    iconColor: 'text-sumoNavy',
    title: 'Track Everything',
    description: 'Units, bins, categories, and SKUs—organized your way.'
  },
  {
    icon: Tags,
    color: 'from-sumoTeal to-blue400',
    iconColor: 'text-white',
    title: 'Manage Listings',
    description: 'eBay, Facebook, and more. One place, zero chaos.'
  },
  {
    icon: TrendingUp,
    color: 'from-success500 to-sumoTeal',
    iconColor: 'text-white',
    title: 'See Your Growth',
    description: 'Revenue, costs, and profit—visualized instantly.'
  },
  {
    icon: Barcode,
    color: 'from-sumoOrange to-sumoPrimary',
    iconColor: 'text-sumoNavy',
    title: 'Print Barcodes',
    description: 'Professional labels in seconds. Avery-ready.'
  },
  {
    icon: Boxes,
    color: 'from-warning500 to-sumoOrange',
    iconColor: 'text-white',
    title: 'Stay Organized',
    description: 'Build your storage hierarchy. Find anything fast.'
  },
  {
    icon: ShoppingCart,
    color: 'from-sumoNavy to-sumoTeal',
    iconColor: 'text-white',
    title: 'Record Sales',
    description: 'Auto-deduct inventory. Track every transaction.'
  }
]

export const FeaturesSection: FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const cardsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view')
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    if (sectionRef.current) observer.observe(sectionRef.current)
    if (cardsRef.current) observer.observe(cardsRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <section 
      id="features" 
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-neutral50 dark:bg-neutral900"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" 
        style={{ 
          backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} 
      />

      <div className="relative max-w-7xl mx-auto">
        {/* Section header - Simplified copy */}
        <div 
          ref={sectionRef}
          className="animate-on-scroll text-center mb-16 md:mb-20 max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral900 dark:text-neutral100 tracking-tight mb-6">
            Everything you need.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sumoPrimary to-sumoAmber">
              Nothing you don't.
            </span>
          </h2>
          <p className="text-lg text-neutral600 dark:text-neutral400 leading-relaxed">
            Powerful features that stay out of your way. Built for speed, designed for clarity.
          </p>
        </div>

        {/* Features grid - More whitespace, cleaner cards */}
        <div 
          ref={cardsRef}
          className="stagger-children grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <div
                key={index}
                className="group card-hover-glow bg-white dark:bg-neutral800 rounded-2xl p-8 border border-neutral200 dark:border-neutral700 hover:border-sumoPrimary/50 hover:shadow-yellow"
              >
                {/* Icon with gradient background */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className={`w-6 h-6 ${feature.iconColor}`} />
                </div>

                {/* Title - concise */}
                <h3 className="text-xl font-semibold text-neutral900 dark:text-neutral100 mb-3">
                  {feature.title}
                </h3>

                {/* Description - scannable, one line ideal */}
                <p className="text-neutral600 dark:text-neutral400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
